import React from "react";
import { CreateProject } from "../../pages";
import SelectProject from "../../components/project/SelectProject";
import { ICustomer, IProject } from "../../@types";
import TooltipDropdown from "../../components/common/tooltipDropdown";
import { getCustomer } from "../../backend-services";
import {
  getProject,
  removeProjectFromExpense,
  removeProjectFromInvoice,
} from "../../backend-services/project";
import { showToast } from "../../utils/toastUtils";
import { DeleteProject } from "../../utils/projects/deleteProject";

export const useProjectHook = ({
  customerId,
  useCustomer,
  projectId,
  useProject,
  expenseId,
  invoiceId,
}: {
  startOfMonth?: Date;
  endOfMonth?: Date;
  customerId?: string | undefined;
  projectId?: string | null;
  invoiceId?: string | null;
  expenseId?: string | null;
  useCustomer?: boolean;
  useProject?: boolean;
}) => {
  const [selectedProject, setSelectedProject] =
    React.useState<IProject | null>();
  const [addToProject, setaddToProject] = React.useState(false);
  const [selectedCustomer, setSelectedCustomer] = React.useState<ICustomer>();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = React.useState(false);
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [isOpenSelectProject, setIsOpenSelectProject] = React.useState(false);

  const openModal = () => setIsOpen(true);
  const openSelectModal = () => {
    setaddToProject(true);
    setIsOpenSelectProject(true);
  };
  const openRemovalModal = () => {
    setIsRemoveModalOpen(true);
  };
  const closeModal = () => setIsOpen(false);

  const fetchcustomer = async () => {
    if (customerId && useCustomer) {
      const data = await getCustomer(customerId);
      data && setSelectedCustomer(data);
    }
  };
  const fetchProject = async () => {
    if (projectId) {
      const data = await getProject(projectId);
      data && setSelectedProject(data);
    }
  };

  const toggleProjectInvoice = async ({
    invoiceId,
    value = false,
    projectId,
    onRefetch,
  }: {
    invoiceId: string;
    projectId?: string;
    value?: boolean;
    onRefetch?: () => Promise<void>;
  }) => {
    setIsRemoving(true);
    try {
      await removeProjectFromInvoice(
        invoiceId,
        projectId || selectedProject?.id || "",
        value,
      );
      onRefetch?.();
      showToast(
        `Project ${value ? "added to" : "removed from"} invoice`,
        "success",
      );
      return true;
    } catch (error) {
      console.error(
        `Failed to ${value ? "add" : "remove"} invoice from project:`,
        error,
      );
      showToast(
        `Failed to ${value ? "add" : "remove"} invoice from project`,
        "error",
      );
      return false;
    } finally {
      setIsRemoving(false);
      setIsRemoveModalOpen(false);
    }
  };
  const toggleProjectExpense = async ({
    expenseId,
    value = false,
    projectId,
    onRefetch,
  }: {
    expenseId: string;
    projectId?: string;
    value?: boolean;
    onRefetch?: () => Promise<void>;
  }) => {
    setIsRemoving(true);
    try {
      await removeProjectFromExpense(
        expenseId,
        projectId || selectedProject?.id || "",
        value,
      );
      if (!value) {
        setSelectedProject(undefined);
      }
      onRefetch?.();
      showToast(
        `Project ${value ? "added to" : "removed from"} expense`,
        "success",
      );
      return true;
    } catch (error) {
      console.error(error);
      showToast(
        `Failed to ${value ? "add" : "remove"} expense from project`,
        "error",
      );
      return false;
    } finally {
      setIsRemoveModalOpen(false);
      setIsRemoving(false);
    }
  };

  React.useEffect(() => {
    if (customerId && useCustomer) {
      fetchcustomer();
    }
  }, [customerId]);

  React.useEffect(() => {
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  const CreateProjectSideBar = ({ close }: { close?: () => void }) => (
    <CreateProject
      onClose={() => {
        closeModal();
      }}
      refetch={() => close?.()}
      addProject={async (id: string) => {
        if (addToProject && expenseId) {
          toggleProjectExpense({ expenseId, value: true, projectId: id });
        } else if (addToProject && invoiceId) {
          toggleProjectInvoice({
            invoiceId,
            value: true,
            projectId: id,
          });
        }
      }}
      isOpen={isOpen}
      setSelectedProject={(project: IProject) => setSelectedProject(project)}
      selectedCustomer={selectedCustomer}
    />
  );

  const SelectProjectModal = () => (
    <SelectProject
      setIsOpen={(value) => setIsOpenSelectProject(value)}
      handleSetProject={(selectedProject) => {
        setSelectedProject(selectedProject);
        setIsOpen(false);
        if (addToProject && expenseId) {
          toggleProjectExpense({
            expenseId,
            value: true,
            projectId: selectedProject?.id,
          });
        } else if (addToProject && invoiceId) {
          toggleProjectInvoice({
            invoiceId,
            value: true,
            projectId: selectedProject?.id,
          });
        }
      }}
      handleAddProject={() => {
        openModal();
        setIsOpenSelectProject(false);
      }}
      isOpenSelectProject={isOpenSelectProject}
      projectCustomerId={selectedCustomer?.id || ""}
    />
  );
  const [isHovered, setIsHovered] = React.useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  let toolTipText = "Add a customer to set up you project.";

  const AddProjectButton = ({ error }: { error: string }) => (
    <div className="mt-5 md:mt-0 w-full">
      <div className="flex gap-1 items-center text-[14px]">
        <p className="text-[14px] font-medium leading-5 mb-1">Add Project</p>
        {toolTipText && (
          <div
            className="relative mb-1"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {toolTipText && (
              <div
                className="relative"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <TooltipDropdown
                  toolTipText={toolTipText}
                  isHovered={isHovered}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className={`w-full cursor-pointer  px-3 flex items-center justify-between h-10 md:h-12 placeholder:text-[16px] border text-[#98A2B3] rounded-lg border-[#D0D5DD] ${error && "border-red-500"} ${selectedProject ? "bg-[#F9FAFB]" : "bg-white"}`}
      >
        <button
          type="button"
          className="flex-1 text-left"
          onClick={() => {
            if (!useCustomer || customerId) {
              setIsOpenSelectProject(true);
            } else {
              window.scrollTo(0, 0);
              showToast("Please select a client.", "info");
            }
          }}
        >
          {selectedProject ? (
            <span className="text-black capitalize">
              {" "}
              {selectedProject.name}
            </span>
          ) : (
            "Add Project"
          )}
        </button>

        {selectedProject && (
          <button type="button" onClick={() => setSelectedProject(null)}>
            {" "}
            <svg
              width={20}
              height={20}
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="#D92D20"
                strokeWidth={2}
                strokeLinecap="square"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
      <p className="text-[red] absolute whitespace-nowrap text-[12px]">
        {error}
      </p>
    </div>
  );
  const RemoveProjectButton = (id: string) => (
    <button
      type="button"
      onClick={() => {
        toggleProjectExpense({ expenseId: id });
      }}
      className="bg-[#FEF3F2] px-2 py-1 rounded-lg text-[#D92D20]"
    >
      Remove
    </button>
  );

  const RemoveProjectButtonInvoice = (id: string) => (
    <div>
      <h1 className="font-semibold text-xl mb-2">{selectedProject?.name}</h1>
      <button
        type="button"
        onClick={() => {
          openRemovalModal();
        }}
        className="md:bg-[#E0EEFE] md:px-3 md:py-2 font-medium text-base rounded text-[#0D3C6D]"
      >
        Remove from project
      </button>
    </div>
  );

  const RemoveProjectModal = ({
    id,
    name = "a",
    onRefetch,
  }: {
    id: string;
    name: string;
    onRefetch?: () => Promise<void>;
  }) => (
    <>
      {isRemoveModalOpen && (
        <DeleteProject
          actionFn={() => {
            if (invoiceId) {
              toggleProjectInvoice({ invoiceId: id, onRefetch });
            } else if (expenseId) {
              toggleProjectExpense({ expenseId: id, onRefetch });
            }
          }}
          cancelFn={() => {
            setIsRemoveModalOpen(false);
          }}
          subTitle={`You are about to remove ${name && name} project. Are you sure you want to
                    continue this action?`}
          title="Remove Project"
          actionText={isRemoving ? "Removing..." : "Yes, Remove"}
          cancelText="Cancel"
        />
      )}
    </>
  );

  return {
    CreateProjectSideBar,
    openModal,
    SelectProjectModal,
    AddProjectButton,
    selectedProject,
    selectedCustomer,
    RemoveProjectButton,
    RemoveProjectButtonInvoice,
    openSelectModal,
    RemoveProjectModal,
    openRemovalModal,
    toggleProjectInvoice,
    toggleProjectExpense,
    isRemoving,
  };
};
